import React, { useEffect, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowRight,
  ArrowUpRight,
  AlertTriangle,
  BadgeCheck,
  BarChart3,
  Brain,
  Building2,
  Calendar,
  Check,
  CheckCircle2,
  ChevronRight,
  CloudUpload,
  Cpu,
  Database,
  Download,
  ExternalLink,
  FileSpreadsheet,
  Gauge,
  Layers,
  Lock,
  Mail,
  MapPin,
  Menu,
  Network,
  PieChart as PieIcon,
  Phone,
  Send,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  Target,
  TrendingUp,
  Upload,
  Users,
  Workflow,
  X,
  Zap,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Toaster, toast } from "sonner";
import axios from "axios";
import AIPreview from "../components/AIPreview";
import {
  Tooltip as UITooltip,
  TooltipContent as UITooltipContent,
  TooltipProvider as UITooltipProvider,
  TooltipTrigger as UITooltipTrigger,
} from "../components/ui/tooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Brand logo — served locally from /public so no external CDN dependencies
const LOGO_URL = "/rciq-logo.png";

/* ============================ NAV ============================ */
const Nav = ({ onUpload, onBookDemo }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMobile = () => setMobileOpen(false);
  const handleMobileNav = (action) => {
    closeMobile();
    if (typeof action === "function") action();
  };

  return (
    <header
      data-testid="site-nav"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen
          ? "backdrop-blur-xl bg-[#050913]/90 border-b border-white/10"
          : "bg-[#050913]/70 md:bg-transparent md:backdrop-blur-0 backdrop-blur-md border-b border-white/5 md:border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-24 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" onClick={closeMobile} className="flex items-center" style={{ touchAction: "manipulation" }}>
          <span className="inline-flex items-center bg-white rounded-lg px-2 py-1 md:px-2.5 md:py-1.5 ring-1 ring-white/10 shadow-[0_4px_18px_-6px_rgba(34,211,238,0.45)]">
            <img
              src={LOGO_URL}
              alt="Revenue Cycle IQ — Insights that drive revenue"
              className="h-8 md:h-12 w-auto object-contain block select-none"
              style={{ imageRendering: "auto" }}
            />
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8 text-sm">
          <a data-testid="nav-link-product" href="#benefits" className="rciq-link">Platform</a>
          <a data-testid="nav-link-dashboard" href="#dashboard" className="rciq-link">Dashboard</a>
          <a data-testid="nav-link-security" href="#security" className="rciq-link">Security</a>
          <a data-testid="nav-link-pricing" href="#pricing" className="rciq-link">Pricing</a>
          <a data-testid="nav-link-faq" href="#faq" className="rciq-link">FAQ</a>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            data-testid="nav-launch-btn"
            type="button"
            onClick={onUpload}
            style={{ touchAction: "manipulation" }}
            className="hidden sm:inline-flex items-center gap-1.5 h-10 px-4 rounded-lg rciq-btn-ghost text-sm font-medium"
          >
            <Zap className="w-4 h-4 text-cyan-300" /> Launch Dashboard
          </button>
          <button
            data-testid="nav-book-demo-btn"
            type="button"
            onClick={onBookDemo}
            style={{ touchAction: "manipulation" }}
            className="hidden md:inline-flex items-center gap-1.5 h-10 px-5 rounded-lg rciq-btn-primary text-sm"
          >
            Request demo <ArrowRight className="w-4 h-4" />
          </button>
          <button
            data-testid="nav-mobile-toggle"
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            style={{ touchAction: "manipulation" }}
            className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-lg border border-white/15 bg-white/5 text-cyan-100 active:bg-white/10"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="nav-mobile-drawer"
        className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
          mobileOpen ? "max-h-[480px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-4 pb-4 pt-1 border-t border-white/10 bg-[#050913]/95 backdrop-blur-xl">
          <nav className="flex flex-col text-sm">
            <a data-testid="m-nav-product" href="#benefits" onClick={closeMobile} className="py-3 px-2 rounded-md text-slate-200 active:bg-white/5">Platform</a>
            <a data-testid="m-nav-dashboard" href="#dashboard" onClick={closeMobile} className="py-3 px-2 rounded-md text-slate-200 active:bg-white/5">Dashboard</a>
            <a data-testid="m-nav-security" href="#security" onClick={closeMobile} className="py-3 px-2 rounded-md text-slate-200 active:bg-white/5">Security</a>
            <a data-testid="m-nav-pricing" href="#pricing" onClick={closeMobile} className="py-3 px-2 rounded-md text-slate-200 active:bg-white/5">Pricing</a>
            <a data-testid="m-nav-faq" href="#faq" onClick={closeMobile} className="py-3 px-2 rounded-md text-slate-200 active:bg-white/5">FAQ</a>
          </nav>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <button
              data-testid="m-nav-launch-btn"
              type="button"
              onClick={() => handleMobileNav(onUpload)}
              style={{ touchAction: "manipulation" }}
              className="inline-flex items-center justify-center gap-1.5 h-11 px-3 rounded-lg rciq-btn-ghost text-sm font-medium"
            >
              <Zap className="w-4 h-4 text-cyan-300" /> Launch
            </button>
            <button
              data-testid="m-nav-book-demo-btn"
              type="button"
              onClick={() => handleMobileNav(onBookDemo)}
              style={{ touchAction: "manipulation" }}
              className="inline-flex items-center justify-center gap-1.5 h-11 px-3 rounded-lg rciq-btn-primary text-sm"
            >
              Request demo <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

/* ====================== HERO DASHBOARD ====================== */
const heroTrend = [
  { m: "Jan", denied: 162, recovered: 92 },
  { m: "Feb", denied: 154, recovered: 118 },
  { m: "Mar", denied: 138, recovered: 144 },
  { m: "Apr", denied: 122, recovered: 172 },
  { m: "May", denied: 108, recovered: 198 },
  { m: "Jun", denied: 96, recovered: 228 },
  { m: "Jul", denied: 81, recovered: 264 },
  { m: "Aug", denied: 72, recovered: 296 },
];
const heroDrivers = [
  { name: "Authorization", v: 32, fill: "#22d3ee" },
  { name: "Med Necessity", v: 22, fill: "#0ea5e9" },
  { name: "Eligibility", v: 18, fill: "#a855f7" },
  { name: "Missing Docs", v: 14, fill: "#f59e0b" },
  { name: "Other", v: 14, fill: "#64748b" },
];

const HeroDashboard = () => (
  <div data-testid="hero-dashboard-mockup" className="relative">
    {/* Floating sub-cards */}
    <div className="hidden lg:flex flex-col items-end absolute -top-6 -left-6 z-20 rciq-float-a">
      <div className="rciq-glass-strong p-4 w-[220px] shadow-2xl shadow-black/40">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-cyan-300 font-semibold">
          <Sparkles className="w-3 h-3" /> AI Insight
        </div>
        <div className="mt-2 text-[12px] text-slate-200 leading-snug">
          Authorization denials trending <span className="text-teal-300 font-semibold">+18% MoM</span> — driven by UMR outpatient claims.
        </div>
      </div>
    </div>

    <div className="hidden lg:flex absolute -bottom-8 -right-6 z-20 rciq-float-b">
      <div className="rciq-glass-strong p-4 w-[230px] shadow-2xl shadow-black/40">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-cyan-300 font-semibold">
          <Target className="w-3 h-3" /> Recovery focus
        </div>
        <div className="mt-2 text-[12px] text-slate-200 leading-snug">
          Front-end eligibility edits could recover <span className="text-teal-300 font-mono-rc font-semibold">$142K</span> this month.
        </div>
      </div>
    </div>

    {/* Main dashboard */}
    <div className="rciq-glass-strong overflow-hidden shadow-2xl shadow-black/50">
      <div className="flex items-center justify-between px-4 h-10 border-b border-white/10 bg-black/30">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-400/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-300/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-cyan-400/80" />
        </div>
        <div className="font-mono-rc text-[11px] text-slate-400">
          intelligence.therevenuecycleiq.com
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-cyan-300 font-semibold">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 rciq-pulse-dot" /> LIVE
        </div>
      </div>

      <div className="p-5 grid grid-cols-12 gap-4">
        <div className="col-span-12 grid grid-cols-2 gap-3">
          {[
            { l: "Recoverable", v: "$2.41M", d: "+18.6%", Icon: TrendingUp },
            { l: "Denial Rate", v: "5.2%", d: "-4.1pp", Icon: AlertTriangle },
          ].map((k) => (
            <div key={k.l} className="rounded-xl bg-white/[0.04] border border-white/10 p-3.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wide text-slate-400">{k.l}</span>
                <k.Icon className="w-3.5 h-3.5 text-cyan-300" />
              </div>
              <div className="mt-1.5 flex items-end justify-between">
                <div className="font-display text-xl font-semibold text-white">{k.v}</div>
                <div className="text-[11px] font-medium text-teal-300 bg-teal-400/10 px-1.5 py-0.5 rounded">
                  {k.d}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="col-span-12 lg:col-span-8 rounded-xl bg-white/[0.03] border border-white/10 p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400">Recovered vs Denied ($K)</div>
              <div className="font-display text-sm font-medium text-slate-100">Last 8 months</div>
            </div>
            <div className="text-[11px] text-cyan-300 font-mono-rc bg-cyan-400/10 px-2 py-1 rounded">
              ↑ recovery
            </div>
          </div>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={heroTrend} margin={{ top: 4, right: 6, bottom: 0, left: -18 }}>
                <defs>
                  <linearGradient id="gRec" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2dd4bf" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#2dd4bf" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gDen" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fb7185" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#fb7185" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="m" stroke="#64748b" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis stroke="#64748b" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#0a1224", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12, color: "#fff" }} />
                <Area type="monotone" dataKey="denied" stroke="#fb7185" strokeWidth={2} fill="url(#gDen)" />
                <Area type="monotone" dataKey="recovered" stroke="#2dd4bf" strokeWidth={2.4} fill="url(#gRec)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-4 rounded-xl bg-white/[0.03] border border-white/10 p-4">
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Top Denial Drivers</div>
          <div className="font-display text-sm font-medium text-slate-100 mb-1">Operational mix</div>
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={heroDrivers} dataKey="v" nameKey="name" cx="50%" cy="50%" innerRadius={28} outerRadius={50} paddingAngle={2} stroke="none">
                  {heroDrivers.map((d, i) => (<Cell key={i} fill={d.fill} />))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-1 space-y-1">
            {heroDrivers.map((d) => (
              <li key={d.name} className="flex items-center justify-between text-[11px] text-slate-300">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-sm" style={{ background: d.fill }} />
                  {d.name}
                </span>
                <span className="font-mono-rc text-slate-200">{d.v}%</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </div>
);

/* =========================== HERO ============================ */
const Hero = ({ onUpload, onBookDemo }) => (
  <section id="top" className="relative pt-24 sm:pt-28 md:pt-40 pb-24 overflow-hidden">
    <div className="rciq-aurora" />
    <div className="rciq-grid" />
    <div className="absolute -top-40 right-1/4 w-[480px] h-[480px] rounded-full pointer-events-none"
         style={{ background: "radial-gradient(closest-side, rgba(45,212,191,0.18), transparent 70%)" }} />
    <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-14 items-center">
        <div className="lg:col-span-6">
          <div className="rciq-rise rciq-chip">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 rciq-pulse-dot" />
            AI Insights That Drive Revenue · No PHI · No EHR/PM Integration Required
          </div>
          <h1
            data-testid="hero-headline"
            className="rciq-rise rciq-delay-1 mt-6 font-display text-4xl sm:text-5xl lg:text-[68px] font-semibold tracking-tight leading-[1.05]"
          >
            <span className="rciq-grad-text">Transform Denials Into</span>
            <br />
            <span className="rciq-teal-text">Revenue Intelligence.</span>
          </h1>
          <p
            data-testid="hero-outcome-promise"
            className="rciq-rise rciq-delay-2 mt-5 text-[15.5px] sm:text-base text-cyan-100/90 max-w-2xl leading-relaxed font-medium"
          >
            Within 30 days, know exactly where your top denial dollars are,
            which payor is your biggest leak, and which denials to work first —
            backed by <span className="text-cyan-300">AI-generated appeals ready to file</span>.
          </p>
          <p className="rciq-rise rciq-delay-2 mt-4 text-sm sm:text-base text-slate-400 max-w-xl leading-relaxed">
            AI-powered denial analytics, KPI tracking, root cause detection, and
            appeal recommendations — without requiring PHI or EHR integration.
          </p>

          <div className="rciq-rise rciq-delay-3 mt-8 flex flex-wrap items-center gap-3">
            <button
              data-testid="hero-demo-btn"
              onClick={onBookDemo}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-lg rciq-btn-primary text-sm"
            >
              Request Demo <ArrowRight className="w-4 h-4" />
            </button>
            <button
              data-testid="hero-launch-btn"
              onClick={onUpload}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-lg rciq-btn-ghost text-sm font-semibold"
            >
              <Zap className="w-4 h-4 text-cyan-300" /> Launch Dashboard
            </button>
          </div>

          <div className="rciq-rise rciq-delay-4 mt-10 flex flex-wrap gap-x-6 gap-y-3 text-[12px] text-slate-400">
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-cyan-400" /> Zero-PHI architecture</span>
            <span className="flex items-center gap-1.5"><FileSpreadsheet className="w-3.5 h-3.5 text-cyan-400" /> CSV & Excel ready</span>
            <span className="flex items-center gap-1.5"><Brain className="w-3.5 h-3.5 text-cyan-400" /> ANSI / CARC / RARC native</span>
          </div>
        </div>

        <div className="lg:col-span-6 rciq-rise rciq-delay-3">
          <HeroDashboard />
        </div>
      </div>
    </div>
  </section>
);

/* ===================== INDUSTRY ANCHOR BAND ==================== */
const IndustryAnchor = ({ onUpload }) => {
  const stats = [
    {
      headline: "2–5%",
      sub: "of net patient revenue lost to denials annually",
    },
    {
      headline: "65% Recoverable",
      sub: "of denied dollars can typically be recovered, yet only 35% are ever resubmitted",
    },
    {
      headline: "$118",
      sub: "average manual rework cost per denied claim",
    },
  ];
  return (
    <section
      data-testid="industry-anchor-section"
      className="relative py-16 md:py-20 border-y border-white/5 bg-[#04080F]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-10 md:mb-12">
          <div className="text-[11px] tracking-[0.22em] uppercase text-cyan-300 font-bold">
            The Cost of Denials Today
          </div>
          <h3 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-white tracking-tight leading-tight">
            Healthcare Organizations Are Losing Revenue to Preventable Denials.
          </h3>
          <p className="mt-4 text-[15.5px] text-slate-100 max-w-2xl mx-auto leading-relaxed">
            Revenue Cycle IQ transforms denial data into actionable recovery
            intelligence — without PHI exposure or EHR integrations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {stats.map((s, i) => (
            <div
              key={s.sub}
              data-testid={`industry-stat-${i}`}
              className="rciq-glass-strong relative overflow-hidden p-8 group transition hover:border-cyan-400/40"
            >
              <div className="relative">
                <div
                  className={`font-display font-bold leading-tight tracking-tight ${s.headline.length > 6 ? "text-[32px] sm:text-[36px]" : "text-[44px] sm:text-[48px]"}`}
                  style={{
                    background:
                      "linear-gradient(135deg, #67e8f9 0%, #22d3ee 60%, #14b8a6 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  {s.headline}
                </div>
                <div className="mt-6 text-[15px] text-slate-100 leading-relaxed font-medium">
                  {s.sub}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-center gap-4">
          <button
            data-testid="industry-anchor-cta"
            onClick={onUpload}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-lg rciq-btn-primary text-sm font-semibold"
          >
            <CloudUpload className="w-4 h-4" />
            Upload Sample Data
          </button>
          <p className="text-[12.5px] text-slate-400 max-w-2xl text-center leading-relaxed text-balance">
            Built on industry research and RCM best practices.
          </p>
        </div>
      </div>
    </section>
  );
};

/* ======================== TRUST/VALUE ========================= */
const TrustValue = () => {
  const stats = [
    { v: "24+", l: "24+ years of operational revenue cycle expertise built into the platform", size: "lg" },
    { v: "ANSI/CARC/RARC Denial Intelligence", l: "Operational denial code coverage across every payor and specialty", size: "sm" },
    { v: "Zero PHI", l: "PHI-free architecture designed for secure operational analytics", size: "lg" },
    { v: "Recoverability Scoring", l: "AI-driven prioritization of denial recovery opportunities", size: "sm" },
  ];
  return (
    <section data-testid="trust-value-section" className="relative py-20 md:py-24 border-y border-white/5 bg-[#040712]">
      <div className="rciq-aurora opacity-30" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <div className="text-xs tracking-[0.22em] uppercase text-cyan-300 font-semibold">
              The platform behind your numbers
            </div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white leading-tight">
              Built By Revenue Cycle Experts.
              <br />
              <span className="rciq-teal-text">Powered By AI.</span>
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed text-[15px] max-w-xl">
              Revenue Cycle IQ combines real-world revenue cycle expertise with
              AI-driven analytics to help organizations reduce denials, improve
              first-pass acceptance, identify operational gaps, and drive
              revenue performance improvement.
            </p>
            <div
              data-testid="human-oversight-callout"
              className="mt-6 max-w-xl rounded-xl border border-cyan-400/30 bg-gradient-to-br from-cyan-400/10 to-emerald-400/5 p-5"
            >
              <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-cyan-300 font-bold mb-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-300" />
                What sets us apart
              </div>
              <p className="text-[14.5px] leading-relaxed text-white">
                Unlike generic AI tools, Revenue Cycle IQ combines automation with{" "}
                <span className="text-cyan-300 font-semibold">active human oversight</span>{" "}
                to continuously refine denial intelligence and workflow recommendations.
              </p>
            </div>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {["Denial Management", "Authorization", "A/R & Collections", "Payor Trends", "Multi-Specialty", "Recoverability Scoring"].map((t) => (
                <span key={t} className="rciq-chip">{t}</span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="grid grid-cols-2 gap-4">
              {stats.map((s, i) => (
                <div key={s.l} className={`rciq-glass p-5 flex flex-col ${i === 0 ? "rciq-rise" : i === 1 ? "rciq-rise rciq-delay-1" : i === 2 ? "rciq-rise rciq-delay-2" : "rciq-rise rciq-delay-3"}`}>
                  <div className={`font-display font-semibold tracking-tight text-white leading-tight ${s.size === "sm" ? "text-lg sm:text-xl" : "text-3xl sm:text-4xl"}`}>
                    {s.v}
                  </div>
                  <div className="mt-2 text-[12.5px] text-slate-400 leading-relaxed">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ======================== BENEFITS ============================ */
const Benefits = () => {
  const items = [
    { Icon: ShieldCheck, t: "No PHI Required", b: "Files with patient identifiers are auto-rejected at upload. Operate entirely on de-identified data." },
    { Icon: Network, t: "No EHR Integration Needed", b: "Stand-alone analytics layer. No IT projects, no security reviews, no clinical-system coupling." },
    { Icon: Brain, t: "AI-Powered Denial Intelligence", b: "Specialty-aware models translate raw codes into operational denial drivers across every line of service." },
    { Icon: Gauge, t: "Executive KPI Visibility", b: "Board-ready snapshots: denial rate, recovery $, AR days, payor scorecards, appeal-win rate." },
    { Icon: Target, t: "Root Cause Analysis", b: "Goes beyond denial codes — clusters denials by upstream cause and recurring pattern." },
    { Icon: Workflow, t: "Appeal Recommendations", b: "Ranks the best-fit appeal action per cohort, payor, and dollar value." },
    { Icon: CloudUpload, t: "Upload Existing Reports", b: "Use the denial exports you already produce. CSV in, intelligence out — within minutes." },
    { Icon: Stethoscope, t: "Multi-Specialty Support", b: "Full ANSI/CARC/RARC coverage across surgical, professional, hospital, ambulatory, and ancillary specialties — with payer- and specialty-aware refinements." },
  ];
  return (
    <section id="benefits" data-testid="benefits-section" className="relative py-24 md:py-28">
      <div className="rciq-aurora opacity-50" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="max-w-2xl">
          <div className="text-xs tracking-[0.22em] uppercase text-cyan-300 font-semibold">Platform</div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">
            Built Around How Revenue Cycle Teams Actually Work.
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((it) => (
            <div
              key={it.t}
              data-testid={`benefit-${it.t.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              className="rciq-card p-6 group"
            >
              <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/20 grid place-items-center text-cyan-300 transition-transform duration-300 group-hover:-translate-y-1">
                <it.Icon className="w-5 h-5" />
              </div>
              <h4 className="mt-4 font-display text-base font-semibold text-white">{it.t}</h4>
              <p className="mt-1.5 text-[13px] text-slate-400 leading-relaxed">{it.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ===================== DASHBOARD PREVIEW ===================== */
const previewWeek = [
  { m: "Wk 1", d: 42 }, { m: "Wk 2", d: 58 }, { m: "Wk 3", d: 51 },
  { m: "Wk 4", d: 67 }, { m: "Wk 5", d: 49 }, { m: "Wk 6", d: 38 },
  { m: "Wk 7", d: 31 }, { m: "Wk 8", d: 27 },
];
const previewPayors = [
  { name: "UMR",       denials: 184, dollars: 412, rate: 7.2, trend: "up" },
  { name: "Aetna",     denials: 142, dollars: 318, rate: 5.9, trend: "down" },
  { name: "BCBS",      denials: 128, dollars: 295, rate: 6.4, trend: "up" },
  { name: "Cigna",     denials: 96,  dollars: 218, rate: 4.8, trend: "flat" },
  { name: "Medicaid",  denials: 88,  dollars: 174, rate: 3.7, trend: "down" },
];
const top5Drivers = [
  { name: "Authorization",      count: 312, dollars: 412000, pct: 28, recovery: 65 },
  { name: "Medical Necessity",  count: 248, dollars: 318000, pct: 22, recovery: 55 },
  { name: "Eligibility",        count: 196, dollars: 244000, pct: 17, recovery: 58 },
  { name: "Missing Documentation", count: 158, dollars: 187000, pct: 14, recovery: 70 },
  { name: "Coordination of Benefits", count: 116, dollars: 142000, pct: 10, recovery: 60 },
];

const fmt$ = (n) => `$${(n / 1000).toFixed(0)}K`;

const DashboardPreview = () => (
  <section id="dashboard" data-testid="dashboard-preview-section" className="relative py-24 md:py-28">
    <div className="rciq-aurora opacity-60" />
    <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
      <div className="max-w-2xl">
          <div className="text-xs tracking-[0.22em] uppercase text-cyan-300 font-semibold">Executive intelligence</div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">
            Executive-Level Revenue Cycle Intelligence.
          </h2>
          <p className="mt-4 text-slate-300 leading-relaxed">
            Top denial categories, payor performance, KPI scorecards, revenue
            impact, root cause detection, and an AI insight feed — all refreshed
            from the file you uploaded. No EHR. No PHI.
          </p>
      </div>

      <div className="mt-12 grid grid-cols-12 gap-4">
        {/* KPI strip */}
        <div className="col-span-12 grid grid-cols-3 gap-3">
          {[
            { l: "Denial Rate", v: "5.2%", d: "-4.1pp", Icon: AlertTriangle },
            { l: "Recoverable $", v: "$2.41M", d: "+18.6%", Icon: TrendingUp },
            { l: "AR Days", v: "31.2", d: "-6.8", Icon: Gauge },
          ].map((k) => (
            <div key={k.l} className="rciq-glass p-4">
              <div className="flex items-center justify-between text-slate-400">
                <span className="text-[10px] uppercase tracking-wide">{k.l}</span>
                <k.Icon className="w-3.5 h-3.5 text-cyan-300" />
              </div>
              <div className="mt-1.5 flex items-end justify-between">
                <div className="font-display text-2xl font-semibold text-white">{k.v}</div>
                <div className="text-[11px] font-medium text-teal-300 bg-teal-400/10 px-1.5 py-0.5 rounded">{k.d}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Top 5 Denial Drivers */}
        <div className="col-span-12 lg:col-span-7 rciq-glass p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-400">Top 5 Denial Drivers</div>
              <div className="font-display text-base font-medium text-white">Operational groupings, not raw codes</div>
            </div>
            <span className="text-[11px] text-cyan-300 font-mono-rc bg-cyan-400/10 px-2 py-1 rounded">90 days</span>
          </div>
          <div className="space-y-2.5">
            {top5Drivers.map((d, i) => (
              <div key={d.name} className="grid grid-cols-12 items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.04] transition">
                <div className="col-span-5 flex items-center gap-2.5">
                  <span className="font-mono-rc text-[10px] text-slate-500">0{i + 1}</span>
                  <span className="text-sm text-white font-medium">{d.name}</span>
                </div>
                <div className="col-span-3 flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-cyan-400 to-teal-300" style={{ width: `${d.pct * 3.2}%` }} />
                  </div>
                  <span className="text-[11px] text-slate-400 font-mono-rc">{d.pct}%</span>
                </div>
                <div className="col-span-2 text-right text-[12px] text-slate-300 font-mono-rc">{d.count}</div>
                <div className="col-span-2 text-right font-mono-rc text-sm text-white font-semibold">${(d.dollars / 1000).toFixed(0)}K</div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-4 text-[10px] uppercase tracking-wide text-slate-500">
            <span>Driver</span><span>% of denials</span><span className="text-right">Count</span><span className="text-right">$ at risk</span>
          </div>
        </div>

        {/* Trend */}
        <div className="col-span-12 lg:col-span-5 rciq-glass p-5">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-400">Denial trend</div>
              <div className="font-display text-base font-medium text-white">Last 8 weeks</div>
            </div>
            <div className="text-[11px] text-cyan-300 font-mono-rc bg-cyan-400/10 px-2 py-1 rounded">↓ 36% WoW avg</div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={previewWeek} margin={{ top: 4, right: 6, bottom: 0, left: -18 }}>
                <defs>
                  <linearGradient id="gWk" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#5eead4" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#5eead4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="m" stroke="#64748b" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis stroke="#64748b" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#0a1224", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12, color: "#fff" }} />
                <Area type="monotone" dataKey="d" stroke="#5eead4" strokeWidth={2.4} fill="url(#gWk)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Payor analysis */}
        <div className="col-span-12 lg:col-span-7 rciq-glass p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-400">Payor Analysis</div>
              <div className="font-display text-base font-medium text-white">Denials by payor</div>
            </div>
          </div>
          <div className="space-y-2">
            {previewPayors.map((p) => (
              <div key={p.name} className="grid grid-cols-12 items-center text-sm py-1.5">
                <div className="col-span-3 text-slate-200">{p.name}</div>
                <div className="col-span-5">
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-cyan-400 to-teal-300" style={{ width: `${(p.denials / 200) * 100}%` }} />
                  </div>
                </div>
                <div className="col-span-2 text-right text-[12px] text-slate-300 font-mono-rc">{p.denials}</div>
                <div className="col-span-2 text-right text-[12px]">
                  <span className={`font-mono-rc px-1.5 py-0.5 rounded ${p.rate >= 6 ? "bg-rose-500/15 text-rose-300" : "bg-cyan-400/10 text-cyan-300"}`}>
                    {p.rate}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Appeal recommendation */}
        <div className="col-span-12 lg:col-span-5 rciq-glass-strong p-5 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-40"
               style={{ background: "radial-gradient(closest-side, rgba(45,212,191,0.6), transparent)" }} />
          <div className="relative">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Appeal Recommendation</div>
            <div className="font-display text-base font-medium text-white">Top opportunity, this week</div>

            <div className="mt-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-cyan-400/15 border border-cyan-400/30 grid place-items-center text-cyan-300">
                <Workflow className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display text-white font-semibold">Bundled CO-197 appeals — Outpatient services</div>
                <div className="text-[12px] text-slate-400">42 claims · UMR · BCBS</div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-white/[0.04] border border-white/10 p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Recovery est.</div>
                <div className="font-display text-lg text-white font-semibold mt-0.5">$84,210</div>
              </div>
              <div className="rounded-lg bg-white/[0.04] border border-white/10 p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Win likelihood</div>
                <div className="font-display text-lg text-teal-300 font-semibold mt-0.5">76%</div>
              </div>
            </div>

            <div className="mt-4 text-[12px] text-slate-300 leading-relaxed">
              These denials share a missing concurrent-review pattern. Generate a
              bundled appeal with attached clinical documentation in one click.
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ======================= HOW IT WORKS ======================== */
const HowItWorks = ({ onUpload }) => {
  const steps = [
    { Icon: CloudUpload, t: "Upload De-Identified Reports", b: "Drop in a denial export from your billing system. PHI columns are auto-rejected before analysis begins." },
    { Icon: Cpu,         t: "AI Detects Trends & Root Causes", b: "Our models translate ANSI/CARC/RARC codes into operational denial drivers and cluster recurring patterns." },
    { Icon: Sparkles,    t: "Generate Executive Insights & Appeal Recommendations", b: "Board-ready dashboards, prioritized worklists, and AI-drafted appeal recommendations — all in minutes." },
    { Icon: TrendingUp,  t: "Improve Collections & Reduce Denials", b: "Recover lost revenue, fix upstream root causes, and watch your denial rate fall quarter-over-quarter." },
  ];
  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="relative py-24 md:py-28">
      <div className="rciq-aurora opacity-40" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="flex items-end flex-wrap gap-6 justify-between">
          <div className="max-w-2xl">
            <div className="text-xs tracking-[0.22em] uppercase text-cyan-300 font-semibold">How it works</div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">
              Simple. Secure. Actionable.
            </h2>
          </div>
          <div className="rciq-chip">
            <Network className="w-3.5 h-3.5" /> No EHR/PM Integration Required
          </div>
        </div>

        <div className="mt-14 grid lg:grid-cols-4 gap-5">
          {steps.map((s, i) => (
            <div key={s.t} className="rciq-card p-6 relative">
              <div className="flex items-center justify-between">
                <span className="font-mono-rc text-xs text-slate-500 tracking-widest">STEP 0{i + 1}</span>
                <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/20 grid place-items-center text-cyan-300">
                  <s.Icon className="w-5 h-5" />
                </div>
              </div>
              <h3 className="mt-5 font-display text-lg font-semibold text-white leading-snug">{s.t}</h3>
              <p className="mt-2.5 text-[13px] text-slate-400 leading-relaxed">{s.b}</p>
              {i < steps.length - 1 && (
                <div className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#050913] border border-white/10 items-center justify-center">
                  <ChevronRight className="w-3.5 h-3.5 text-cyan-300" />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <button
            data-testid="how-upload-btn"
            onClick={onUpload}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-lg rciq-btn-primary text-sm"
          >
            <Upload className="w-4 h-4" /> Try it with a sample file
          </button>
          <span
            data-testid="preview-row-limit-badge"
            className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-gradient-to-r from-cyan-400/15 to-emerald-400/15 border border-cyan-400/40 text-[10px] font-bold uppercase tracking-[0.16em] text-cyan-200"
          >
            <Zap className="w-3 h-3 text-cyan-300" /> 150 Record Limit
          </span>
          <span className="text-[12px] text-slate-500">
            Free one-time preview · No PHI allowed · Runs entirely in your browser.
          </span>
        </div>
      </div>
    </section>
  );
};

/* ======================= AI INSIGHTS ========================= */
const AIInsights = () => {
  const messages = [
    {
      role: "assistant",
      ts: "09:42",
      text:
        "Authorization denials increased 18% month-over-month, primarily driven by UMR outpatient claims. The dominant CARC is CO-197 with a missing concurrent-review pattern.",
    },
    {
      role: "metric",
      label: "Estimated monthly cash impact",
      value: "$142,000",
      delta: "+18% MoM",
    },
    {
      role: "assistant",
      ts: "09:42",
      text:
        "Recommended operational focus: tighten front-end eligibility edits and rework the outpatient authorization workflow. Bundled appeals could recover an estimated $84K this cycle.",
    },
    {
      role: "actions",
      items: [
        { Icon: Workflow, t: "Generate bundled CO-197 appeal pack" },
        { Icon: Target,   t: "Open authorization root-cause drill-down" },
        { Icon: Download, t: "Export executive denial brief (PDF)" },
      ],
    },
  ];
  return (
    <section data-testid="ai-insights-section" className="relative py-24 md:py-28">
      <div className="rciq-aurora opacity-60" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.22em] uppercase text-cyan-300 font-semibold">AI Insight Feed</div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">
              AI That Explains The Why.
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              Every time you upload, Revenue Cycle IQ explains what changed,
              quantifies the dollar impact, and tells your team exactly what to
              go fix this week — in plain English.
            </p>
            <ul className="mt-8 space-y-3.5">
              {[
                { Icon: Brain,    t: "Plain-English explanations, not chart noise." },
                { Icon: Target,   t: "Quantified cash impact for every trend." },
                { Icon: Workflow, t: "Concrete next-actions, ranked by recovery $." },
              ].map((b) => (
                <li key={b.t} className="flex items-start gap-3 text-sm text-slate-300">
                  <div className="w-8 h-8 rounded-lg bg-cyan-400/10 border border-cyan-400/20 grid place-items-center text-cyan-300 shrink-0">
                    <b.Icon className="w-4 h-4" />
                  </div>
                  {b.t}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div className="rciq-glass-strong p-5 sm:p-6 shadow-2xl shadow-black/40">
              <div className="flex items-center justify-between pb-4 border-b border-white/10">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-300 to-cyan-700 grid place-items-center">
                    <Sparkles className="w-4.5 h-4.5 text-slate-900" />
                  </div>
                  <div>
                    <div className="font-display text-white font-semibold text-sm">RCQ Intelligence</div>
                    <div className="text-[11px] text-slate-400">Analyzing 14,283 denial lines · updated 4 min ago</div>
                  </div>
                </div>
                <span className="rciq-chip">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 rciq-pulse-dot" /> Live
                </span>
              </div>

              <div className="mt-5 space-y-4">
                {messages.map((m, i) => {
                  if (m.role === "assistant") {
                    return (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-cyan-400/15 border border-cyan-400/25 grid place-items-center text-cyan-300 shrink-0">
                          <Brain className="w-4 h-4" />
                        </div>
                        <div className="flex-1 rounded-2xl rounded-tl-md bg-white/[0.05] border border-white/10 p-3.5">
                          <div className="text-[11px] text-slate-500 mb-1 font-mono-rc">RCQ · {m.ts}</div>
                          <div className="text-[13.5px] text-slate-100 leading-relaxed">{m.text}</div>
                        </div>
                      </div>
                    );
                  }
                  if (m.role === "metric") {
                    return (
                      <div key={i} className="ml-11 rounded-2xl bg-gradient-to-br from-teal-500/15 to-cyan-500/10 border border-teal-400/25 p-4">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300 font-semibold">{m.label}</div>
                        <div className="mt-1.5 flex items-baseline gap-3">
                          <span className="font-display text-3xl font-semibold text-white">{m.value}</span>
                          <span className="text-[12px] font-mono-rc text-teal-300">{m.delta}</span>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={i} className="ml-11 grid sm:grid-cols-3 gap-2.5">
                      {m.items.map((a) => (
                        <button
                          key={a.t}
                          className="text-left rounded-xl bg-white/[0.04] border border-white/10 hover:border-cyan-400/40 hover:bg-white/[0.08] p-3 transition text-[12.5px] text-slate-200 flex items-start gap-2.5"
                        >
                          <a.Icon className="w-4 h-4 text-cyan-300 mt-0.5 shrink-0" />
                          {a.t}
                        </button>
                      ))}
                    </div>
                  );
                })}

                <div className="flex items-center gap-2 pt-4 border-t border-white/10">
                  <div className="flex-1 rounded-lg bg-white/[0.04] border border-white/10 px-3.5 py-2.5 text-[12.5px] text-slate-500">
                    Ask RCQ anything about your denials…
                  </div>
                  <button className="h-10 w-10 rounded-lg rciq-btn-primary grid place-items-center">
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ====================== SECURITY ============================ */
const Security = () => {
  const items = [
    { Icon: ShieldCheck, t: "No PHI uploads permitted", b: "Files containing patient identifiers are automatically rejected before analysis begins." },
    { Icon: Brain,       t: "Automated PHI detection", b: "ML-based column inspection plus a curated PHI lexicon catch identifiers before they leave your browser." },
    { Icon: FileSpreadsheet, t: "De-identified reporting only", b: "Designed exclusively for operational denial analysis — never patient charts." },
    { Icon: Lock,        t: "Secure cloud architecture", b: "TLS 1.3 in transit, AES-256 at rest, scoped IAM, full audit log of every upload." },
    { Icon: BadgeCheck,  t: "HIPAA-conscious workflow", b: "Engineered to keep teams on the right side of HIPAA — by removing the surface area entirely." },
    { Icon: Users,       t: "Role-based access controls", b: "Granular RBAC with SSO support. View, analyst, and admin roles map to your existing org chart." },
    { Icon: Cpu,         t: "Encrypted data processing", b: "Workloads run in isolated tenants with key-scoped encryption — your data never co-mingles with another customer's." },
    { Icon: Network,     t: "Zero EHR coupling", b: "No connectors. No clinical-system queries. No production access risk to your patient systems." },
  ];
  return (
    <section id="security" data-testid="security-section" className="relative py-24 md:py-28">
      <div className="rciq-aurora opacity-50" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="max-w-2xl">
          <div className="text-xs tracking-[0.22em] uppercase text-cyan-300 font-semibold">Security & Compliance</div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">
            Designed With Security & Compliance In Mind.
          </h2>
          <p className="mt-4 text-slate-300 leading-relaxed">
            Revenue Cycle IQ is intentionally engineered to never see PHI. No
            integrations into your patient systems. No exposure to HIPAA
            obligations you don&apos;t want to inherit.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it) => (
            <div key={it.t} className="rciq-card p-6">
              <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/20 grid place-items-center text-cyan-300">
                <it.Icon className="w-5 h-5" />
              </div>
              <h4 className="mt-4 font-display text-base font-semibold text-white">{it.t}</h4>
              <p className="mt-1.5 text-[13px] text-slate-400 leading-relaxed">{it.b}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 rciq-glass px-5 py-4 text-sm text-slate-300 flex items-start gap-3">
          <ShieldCheck className="w-4 h-4 mt-0.5 text-cyan-300 shrink-0" />
          <p>
            <b className="text-white">Disclaimer:</b> Revenue Cycle IQ is an
            upload-based denial analytics platform. It is not an EHR integration
            platform, does not connect to live patient systems, and does not
            accept PHI. Files with patient identifiers are automatically rejected.
          </p>
        </div>
      </div>
    </section>
  );
};

/* ========================= PRICING =========================== */
// 💳 Stripe Payment Links — replace these placeholders with your real links from Stripe Dashboard.
// Create one Payment Link per plan × billing-cycle combination.
const STRIPE_LINKS = {
  starter: {
    monthly: "https://buy.stripe.com/dRm4gz9rB2fd8eUgsvdIA05",
    annual: "https://buy.stripe.com/28E00j47hdXV8eU2BFdIA04",
  },
  professional: {
    monthly: "https://buy.stripe.com/dRm6oH9rB4nlbr6903dIA03",
    annual: "https://buy.stripe.com/cNi7sL5blaLJdze3FJdIA02",
  },
  bundle: {
    monthly: "https://buy.stripe.com/00wfZhbzJ3jhan21xBdIA01",
    annual: "https://buy.stripe.com/6oU28r7jt7zx66MccfdIA00",
  },
};

const annualPrice = (monthly) => Math.round(monthly * 0.85);

const Pricing = ({ onBookDemo, onUpload, onCheckout }) => {
  const [billing, setBilling] = useState("monthly"); // monthly | annual
  const isAnnual = billing === "annual";

  const tiers = [
    {
      key: "starter",
      name: "Starter",
      blurb: "For independent practices and provider groups",
      monthlyPrice: 799,
      features: [
        { t: "Up to 2,500 denials / month" },
        { t: "Top-5 denial driver analytics" },
        { t: "AI appeal recommendations (50/mo)" },
        { t: "Email support" },
        { t: "1 Monthly RCM Consulting Hour", sub: "Monthly denial & KPI guidance session" },
        { t: "Zero-PHI architecture" },
      ],
      cta: "Subscribe Now",
      highlight: false,
      checkout: true,
      fallbackAction: onUpload,
    },
    {
      key: "professional",
      name: "Professional",
      blurb: "For growing healthcare organizations",
      monthlyPrice: 1499,
      features: [
        { t: "Up to 25,000 denials / month" },
        { t: "Full root-cause analytics" },
        { t: "Unlimited AI appeal recommendations" },
        { t: "Payor scorecards + executive PDFs" },
        { t: "AR Trending & Historical Analysis" },
        { t: "5 user seats · priority support" },
        { t: "3 Monthly RCM Consulting Hours" },
        { t: "Monthly KPI & denial strategy review" },
      ],
      cta: "Subscribe Now",
      highlight: true,
      checkout: true,
      fallbackAction: onBookDemo,
    },
    {
      key: "custom",
      name: "Custom",
      blurb: "For large healthcare systems and complex organizations",
      priceLabel: "Custom",
      unitLabel: "annual",
      features: [
        { t: "Everything in Professional, plus:", sub: "All Professional features included" },
        { t: "Unlimited denial intelligence analysis" },
        { t: "Enterprise-wide dashboard access" },
        { t: "Executive-level RCM operational assessments" },
        { t: "Dedicated RCM consulting & operational advisory" },
        { t: "Monthly executive strategy sessions" },
        { t: "Workflow optimization & denial reduction planning" },
        { t: "Custom KPI scorecards & executive summaries" },
        { t: "Priority onboarding & strategic support" },
      ],
      cta: "Contact Sales",
      highlight: false,
      checkout: false,
      fallbackAction: onBookDemo,
    },
  ];

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-24 md:py-28">
      <div className="rciq-aurora opacity-50" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="max-w-2xl">
          <div className="text-xs tracking-[0.22em] uppercase text-cyan-300 font-semibold">Pricing</div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">
            Flexible Pricing For Every Organization.
          </h2>
          <p className="mt-4 text-slate-300">
            Zero-PHI architecture on every tier. Typical ROI: 12–18× within the first quarter.
          </p>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2.5">
          <span data-testid="pricing-pill-annual" className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-amber-300/30 bg-amber-300/[0.06] text-amber-200 text-xs sm:text-[13px] font-medium">
            <Sparkles className="w-3.5 h-3.5 text-amber-300" />
            <span className="text-amber-200 font-semibold">Save 15%</span>
            <span className="text-amber-200/80">with annual prepay across every plan</span>
          </span>
          <button
            data-testid="pricing-pill-bundle"
            type="button"
            onClick={() => onCheckout({ key: "bundle", name: "Professional Bundle", billing, price: isAnnual ? annualPrice(2199) : 2199 })}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-teal-400/40 bg-teal-400/[0.10] text-teal-200 text-xs sm:text-[13px] font-medium hover:bg-teal-400/[0.18] hover:border-teal-400/60 transition cursor-pointer"
          >
            <Layers className="w-3.5 h-3.5 text-teal-300" />
            <span className="text-teal-200 font-semibold">Bundle &amp; Save</span>
            <span className="text-teal-100/85">— Professional + Revenue Forecast for</span>
            <span className="text-teal-200 font-bold">{isAnnual ? `$${annualPrice(2199).toLocaleString()}/mo billed annually` : "$2,199/mo"}</span>
            <ArrowRight className="w-3.5 h-3.5 text-teal-300" />
          </button>
        </div>

        {/* Billing toggle */}
        <div className="mt-10 flex justify-center">
          <div data-testid="billing-toggle" className="inline-flex items-center gap-1 p-1 rounded-full bg-white/[0.04] border border-white/10">
            <button
              data-testid="billing-toggle-monthly"
              type="button"
              onClick={() => setBilling("monthly")}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition ${billing === "monthly" ? "bg-gradient-to-r from-cyan-400 to-teal-400 text-slate-900 shadow-[0_4px_14px_-4px_rgba(45,212,191,0.5)]" : "text-slate-300 hover:text-white"}`}
            >
              Monthly
            </button>
            <button
              data-testid="billing-toggle-annual"
              type="button"
              onClick={() => setBilling("annual")}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition inline-flex items-center gap-2 ${billing === "annual" ? "bg-gradient-to-r from-cyan-400 to-teal-400 text-slate-900 shadow-[0_4px_14px_-4px_rgba(45,212,191,0.5)]" : "text-slate-300 hover:text-white"}`}
            >
              Annual
              <span className={`text-[10px] tracking-wide font-bold px-2 py-0.5 rounded-full ${billing === "annual" ? "bg-slate-900/20 text-slate-900" : "bg-amber-300/15 text-amber-200"}`}>SAVE 15%</span>
            </button>
          </div>
        </div>

        <div className="mt-12 grid lg:grid-cols-3 gap-6 lg:gap-5 items-stretch">
          {tiers.map((t) => {
            const displayPrice = t.priceLabel
              ? t.priceLabel
              : isAnnual
              ? `$${annualPrice(t.monthlyPrice).toLocaleString()}`
              : `$${t.monthlyPrice.toLocaleString()}`;
            const displayUnit = t.unitLabel
              ? t.unitLabel
              : isAnnual
              ? "/mo · billed annually"
              : "/ month";
            const handleClick = () => {
              if (t.checkout) {
                onCheckout({
                  key: t.key,
                  name: t.name,
                  billing,
                  price: isAnnual ? annualPrice(t.monthlyPrice) : t.monthlyPrice,
                });
              } else {
                t.fallbackAction && t.fallbackAction();
              }
            };
            return (
            <div
              key={t.name}
              data-testid={`pricing-card-${t.name.toLowerCase()}`}
              className={`relative rounded-2xl p-8 pt-9 flex flex-col transition-all duration-300 ${
                t.highlight
                  ? "lg:-mt-6 lg:mb-2 bg-gradient-to-b from-teal-500/[0.10] via-cyan-500/[0.05] to-transparent border border-teal-400/40 ring-1 ring-teal-400/30 shadow-[0_30px_80px_-20px_rgba(45,212,191,0.45),0_0_60px_-10px_rgba(34,211,238,0.30)]"
                  : "rciq-card"
              }`}
            >
              {t.highlight && (
                <>
                  {/* Floating MOST POPULAR pill */}
                  <div className="absolute left-1/2 -translate-x-1/2 -top-5 z-10">
                    <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#050913] border border-teal-400/60 text-teal-200 text-[10px] tracking-[0.22em] font-bold uppercase shadow-[0_8px_24px_-6px_rgba(45,212,191,0.6),inset_0_0_0_1px_rgba(45,212,191,0.15)]">
                      <Sparkles className="w-3 h-3 text-teal-300" />
                      Most Popular
                    </span>
                  </div>
                  {/* Inner teal aura glow */}
                  <div className="absolute inset-0 rounded-2xl pointer-events-none overflow-hidden">
                    <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[140%] h-48 bg-teal-400/15 blur-3xl rounded-full" />
                  </div>
                </>
              )}

              <div className="relative">
                <h3 className={`font-display font-semibold tracking-tight ${t.highlight ? "text-3xl sm:text-4xl text-white" : "text-2xl sm:text-3xl text-white"}`}>
                  {t.name}
                </h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{t.blurb}</p>

                <div className="mt-6 flex items-baseline gap-2 flex-wrap">
                  <span className={`font-display font-bold tracking-tight ${t.highlight ? "text-5xl rciq-teal-text" : "text-4xl text-white"}`}>{displayPrice}</span>
                  <span className="text-sm text-slate-400">{displayUnit}</span>
                </div>
                {t.monthlyPrice && isAnnual && (
                  <div className="mt-1 text-[11.5px] text-amber-200/85 font-medium">
                    Save ${((t.monthlyPrice - annualPrice(t.monthlyPrice)) * 12).toLocaleString()} per year vs monthly
                  </div>
                )}
              </div>

              <ul className="mt-7 space-y-3 relative">
                {t.features.map((f) => (
                  <li key={f.t} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${t.highlight ? "text-teal-300" : "text-teal-300/80"}`} />
                    <span>
                      {f.t}
                      {f.sub && (
                        <span className="block mt-0.5 text-[11.5px] text-slate-500 leading-snug">
                          {f.sub}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                type="button"
                onClick={handleClick}
                className="mt-auto pt-8 relative"
              >
                <span className={`block w-full h-11 leading-[44px] rounded-lg font-semibold text-sm transition ${
                  t.highlight ? "rciq-btn-primary" : "rciq-btn-ghost"
                }`}>
                  {t.cta}
                </span>
              </button>
            </div>
            );
          })}
        </div>

        <p
          data-testid="pricing-advisory-note"
          className="mt-8 text-center text-xs text-slate-500 tracking-wide"
        >
          Additional consulting/advisory hours available upon request.
        </p>
      </div>
    </section>
  );
};

/* ============================== AR TRENDS ====================== */
const ARTrends = ({ onBookDemo }) => {
  // 6-month trendline: AR days falling from 92 → 56.5
  const trend = [
    { m: "Jan", ar: 92, denial: 12.4 },
    { m: "Feb", ar: 84, denial: 11.1 },
    { m: "Mar", ar: 76, denial: 9.8 },
    { m: "Apr", ar: 69, denial: 8.6 },
    { m: "May", ar: 61, denial: 7.4 },
    { m: "Jun", ar: 56.5, denial: 6.2 },
  ];
  const cards = [
    {
      Icon: Calendar,
      title: "Cross-upload tracking",
      body: "Upload monthly or quarterly — we automatically stitch your data together. No EHR integration. No analyst required.",
    },
    {
      Icon: ShieldCheck,
      title: "PHI-free by design",
      body: "Trends are computed from de-identified operational aggregates only. No patient identifiers ever stored. HIPAA-aligned posture.",
    },
    {
      Icon: TrendingUp,
      title: "Snapshot library",
      body: "Every upload is preserved forever with a full audit trail. Compare any month to any other month — perfect for board reviews and renewals.",
    },
  ];
  return (
    <section id="ar-trends" data-testid="ar-trends-section" className="relative py-24 md:py-28">
      <div className="rciq-aurora opacity-50" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="max-w-3xl rciq-rise">
          <div className="text-xs tracking-[0.22em] uppercase text-teal-300 font-semibold">Historical Performance Intelligence</div>
          <h2 data-testid="ar-trends-headline" className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
            <span className="rciq-grad-text">Track Revenue Cycle</span>{" "}
            <span className="rciq-teal-text">Performance Over Time.</span>
          </h2>
          <p className="mt-5 text-[15px] sm:text-base text-slate-300 leading-relaxed max-w-2xl">
            Every upload becomes a permanent reporting snapshot, allowing leadership teams to measure trends in Days in AR, Denial Rate, Cash Collections, and other key performance indicators across months, quarters, and years. Monitor progress, identify emerging risks, and demonstrate measurable operational improvement over time.
          </p>
        </div>

        {/* PROOF STAT + CHART */}
        <div className="rciq-rise rciq-delay-2 mt-12 grid lg:grid-cols-12 gap-6">
          {/* Proof stat callout */}
          <div data-testid="ar-trends-proof" className="lg:col-span-5 relative overflow-hidden rounded-2xl border border-teal-400/25 bg-[linear-gradient(180deg,#06192a_0%,#040c1a_100%)] p-7">
            <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-cyan-400 via-teal-400 to-cyan-400" />
            <div className="text-[11px] tracking-[0.22em] uppercase text-teal-300 font-semibold">Working Capital Reclaimed</div>
            <div className="mt-3 flex items-baseline gap-3">
              <div className="font-display text-5xl font-bold rciq-teal-text leading-none">56.5</div>
              <div className="text-sm text-slate-300 font-medium">Days in AR</div>
            </div>
            <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-emerald-300 font-medium">
              <Check className="w-4 h-4" />
              Reduced from 92 Days in AR
            </div>
            <div className="mt-5 pt-5 border-t border-white/8">
              <div className="text-2xl sm:text-3xl font-display font-semibold text-white tracking-tight">
                $1.3M <span className="text-slate-400 text-base font-normal">Estimated Working Capital Released</span>
              </div>
              <div className="mt-1 text-xs text-slate-500">Based on improved AR performance</div>
            </div>
          </div>

          {/* Chart */}
          <div className="lg:col-span-7 rciq-glass p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-cyan-300/80 font-semibold">Trendline · Last 6 Months</div>
                <div className="font-display font-semibold text-white text-base mt-0.5">Historical Revenue Cycle Performance</div>
              </div>
              <div className="flex items-center gap-4 text-[11px] text-slate-400">
                <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-teal-400" /> AR days</span>
                <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-cyan-400/70" /> Denial %</span>
              </div>
            </div>
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend} margin={{ top: 8, right: 14, left: -22, bottom: 0 }}>
                  <defs>
                    <linearGradient id="arGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2dd4bf" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#2dd4bf" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="m" stroke="#64748b" tickLine={false} axisLine={false} fontSize={11} />
                  <YAxis stroke="#64748b" tickLine={false} axisLine={false} fontSize={11} domain={[40, 100]} />
                  <Tooltip
                    contentStyle={{ background: "rgba(5,9,19,0.95)", border: "1px solid rgba(45,212,191,0.25)", borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: "#67e8f9" }}
                  />
                  <Area type="monotone" dataKey="ar" stroke="none" fill="url(#arGrad)" />
                  <Line type="monotone" dataKey="ar" stroke="#2dd4bf" strokeWidth={2.5} dot={{ r: 3, fill: "#2dd4bf", strokeWidth: 0 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="denial" stroke="#22d3ee" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* EXECUTIVE VALUE CALLOUT */}
        <div data-testid="ar-trends-exec-row" className="rciq-rise rciq-delay-3 mt-6 rciq-glass p-4 sm:p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {[
              "Compare Month-over-Month Performance",
              "Track Quarterly Improvement Trends",
              "Measure Long-Term KPI Performance",
              "Generate Board-Ready Trend Reporting",
            ].map((t) => (
              <div key={t} data-testid={`ar-exec-value-${t.split(" ")[0].toLowerCase()}`} className="flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-md grid place-items-center bg-teal-400/15 border border-teal-400/30 text-teal-300 shrink-0 mt-0.5">
                  <Check className="w-3 h-3" strokeWidth={3} />
                </div>
                <div className="text-[13px] leading-snug text-slate-200 font-medium">{t}</div>
              </div>
            ))}
          </div>
        </div>

        {/* 3 FEATURE CARDS */}
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {cards.map(({ Icon, title, body }, i) => (
            <div
              key={title}
              data-testid={`ar-trends-card-${i}`}
              className={`rciq-card p-6 relative overflow-hidden rciq-rise rciq-delay-${i + 2}`}
            >
              <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-teal-400 via-cyan-400 to-teal-400/40" />
              <div className="w-11 h-11 rounded-lg grid place-items-center bg-teal-400/10 border border-teal-400/25 text-teal-300">
                <Icon className="w-5 h-5" />
              </div>
              <div className="mt-4 font-display font-semibold text-white text-lg tracking-tight">{title}</div>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 flex flex-col items-center text-center gap-3">
          <button
            data-testid="ar-trends-cta"
            type="button"
            onClick={onBookDemo}
            className="inline-flex items-center gap-2 h-12 px-6 rounded-lg rciq-btn-primary text-sm font-semibold"
          >
            See AR Trends in your data — Request a 30-min demo
            <ArrowRight className="w-4 h-4" />
          </button>
          <a
            data-testid="ar-trends-guide-link"
            href="https://dashboard.therevenuecycleiq.com/RCIQ_Denial_Template_Guide.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[12.5px] text-slate-400 hover:text-teal-300 inline-flex items-center gap-1.5 transition"
          >
            Or download the field guide PDF
            <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </section>
  );
};

/* ============================== FAQ ========================== */
const FAQ = () => {
  const items = [
    { q: "We already have a denial dashboard in our EHR or clearinghouse — why do we need this?", a: "Those systems tell you what denied. We tell you what's recoverable — and what to do about it." },
    { q: "How is this different from the AI my EHR vendor is adding?", a: "EHR AI is a checkbox feature. We're a focused product built by people who've worked denials. Our human-supervised model continuously refines recommendations." },
    { q: "Why should I trust an AI tool with revenue cycle decisions?", a: "You shouldn't trust AI alone. That's why ours is human-supervised — every recommendation is monitored and refined by real RCM experts." },
    { q: "Do you store, process, or transmit PHI?", a: "No. Revenue Cycle IQ is architected to make PHI storage impossible. Files containing patient identifiers (names, MRNs, DOBs, SSNs, addresses, phone numbers) are automatically rejected at upload — they never reach our servers, never get analyzed, and never get stored. We are a de-identified operational analytics platform, not a system of record." },
    { q: "Do we need a BAA (Business Associate Agreement) with you?", a: "No. Because we never receive, store, or process PHI, we are not a Business Associate under HIPAA. There is no BAA required — which means no legal review cycle, no security questionnaire round-trip, and no procurement delay. You can be live in days, not quarters." },
    { q: "What happens if someone on my team accidentally uploads a file with PHI?", a: "The file is rejected at the browser level before it ever leaves your network. Our automated PHI detection runs in two layers: (1) an ML-based column inspection, and (2) a curated PHI lexicon that catches identifiers like names, DOBs, MRNs, member IDs, and SSNs. Rejected files are never persisted, never logged, and never seen by us." },
    { q: "How is this safer than putting denial data into ChatGPT or Claude?", a: "Generic AI tools have no concept of PHI vs. operational data. Even when you \"promise not to upload PHI,\" there's no enforcement — one careless paste exposes your organization. Revenue Cycle IQ enforces de-identification at the architectural level: no PHI ever passes the upload gate, full stop. That makes us safe to roll out to entire revenue cycle teams without the OCR risk." },
    { q: "What's your audit posture if we get audited?", a: "Every file upload, user action, login, and permission change is captured in an append-only, tamper-evident audit log retained indefinitely. Combined with our zero-PHI architecture, this gives your compliance officer a clean story: we never had PHI, and every operational event is logged for review." },
    { q: "Does Revenue Cycle IQ touch our EHR or live patient systems?", a: "No. This is intentionally a standalone, upload-based analytics dashboard. There are no EHR connectors and we never query live patient systems. You upload de-identified denial files; we analyze them." },
    { q: "What file formats do you accept?", a: "Standard CSV and Excel (.xlsx) exports — typically from your billing system, clearinghouse, or denial workbooks. CSV is parsed for PHI in the browser preview; Excel is parsed server-side after a demo handshake." },
    { q: "Can this be used in all specialties?", a: "Yes. Revenue Cycle IQ is designed for multi-specialty revenue cycle teams — surgical, professional, hospital, ambulatory, behavioral health, and ancillary services all use the same ANSI/CARC/RARC engine. Specialty- and payer-aware refinements (modifier patterns, place-of-service rules, level-of-care logic, etc.) fire only when relevant to your uploaded data." },
    { q: "How is this different from a BI tool?", a: "BI tools show charts. Revenue Cycle IQ identifies the operational driver behind each denial, ranks recoverable dollars, and recommends the specific appeal action — denial-by-denial." },
    { q: "How fast is implementation?", a: "Most teams produce their first executive denial-intelligence report within an hour of their first upload." },
  ];
  return (
    <section id="faq" data-testid="faq-section" className="relative py-24 md:py-28">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": items.map((it) => ({
              "@type": "Question",
              "name": it.q,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": it.a,
              },
            })),
          }),
        }}
      />
      <div className="rciq-aurora opacity-40" />
      <div className="max-w-5xl mx-auto px-6 lg:px-8 relative">
        <div className="max-w-2xl">
          <div className="text-xs tracking-[0.22em] uppercase text-cyan-300 font-semibold">FAQ</div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">Questions, Answered.</h2>
        </div>
        <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
          {items.map((it, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display font-medium text-white hover:no-underline py-5 text-base">
                {it.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-300 leading-relaxed pb-5">
                {it.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

/* ========================== FINAL CTA ======================== */
const FinalCTA = ({ onUpload, onBookDemo }) => (
  <section data-testid="final-cta" className="relative py-20 md:py-24">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <div className="relative rounded-3xl overflow-hidden rciq-glass-strong p-10 md:p-14">
        <div className="absolute inset-0 opacity-90 pointer-events-none"
             style={{
               background:
                 "radial-gradient(60% 50% at 80% 0%, rgba(45,212,191,0.18) 0%, rgba(45,212,191,0) 60%), radial-gradient(50% 40% at 10% 100%, rgba(14,165,233,0.18) 0%, rgba(14,165,233,0) 60%)",
             }} />
        <div className="absolute inset-0 opacity-30 rciq-sweep pointer-events-none" />
        <div className="relative grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="text-[11px] tracking-[0.22em] uppercase text-cyan-300 font-semibold">
              Ready to operationalize your denials
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
              Turn Revenue Cycle Data Into Actionable Intelligence.
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed max-w-xl text-[15px]">
              Reduce denials. Improve visibility. Drive smarter operational decisions.
            </p>
            <p className="mt-3 text-slate-400 leading-relaxed max-w-xl text-sm">
              Schedule a 20-minute walkthrough on your data — or start a free
              trial and have your first executive denial brief by tomorrow.
            </p>
          </div>
          <div className="md:col-span-4 flex md:flex-col gap-3">
            <button
              data-testid="final-cta-demo-btn"
              onClick={onBookDemo}
              className="h-12 px-5 rounded-lg rciq-btn-primary text-sm font-semibold w-full inline-flex items-center justify-center gap-2"
            >
              Schedule Demo <ArrowRight className="w-4 h-4" />
            </button>
            <button
              data-testid="final-cta-enterprise-btn"
              onClick={onBookDemo}
              className="h-12 px-5 rounded-lg rciq-btn-ghost text-sm font-semibold w-full inline-flex items-center justify-center gap-2"
            >
              Request Enterprise Access
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ========================== CONTACT ========================== */
const Contact = () => {
  const [form, setForm] = useState({
    name: "", email: "", organization: "", role: "",
    phone: "", organization_size: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.organization) {
      toast.error("Please fill in your name, email, and organization.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await axios.post(`${API}/demo-requests`, payload);
      setSubmitted(true);
      toast.success("Thanks! Our team will be in touch within 1 business day.");
      setForm({ name: "", email: "", organization: "", role: "", phone: "", organization_size: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-28">
      <div className="rciq-aurora opacity-40" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-xs tracking-[0.22em] uppercase text-cyan-300 font-semibold">Request a demo</div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">
            See Your Own Denial Data — Without Giving Us A Drop Of PHI.
          </h2>
          <p className="mt-5 text-slate-300 leading-relaxed">
            Tell us about your organization and we&apos;ll walk you through the
            dashboard with a de-identified sample of your data. You&apos;ll leave
            with a sized estimate of your recoverable revenue.
          </p>

          <ul className="mt-8 space-y-4">
            {[
              { Icon: ShieldCheck, t: "Zero PHI required. PHI-containing files auto-rejected." },
              { Icon: Lock, t: "Built for multi-specialty revenue cycle teams." },
              { Icon: TrendingUp, t: "Walk away with a recoverable-revenue estimate." },
            ].map((b) => (
              <li key={b.t} className="flex items-start gap-3 text-sm text-slate-300">
                <div className="w-8 h-8 rounded-lg bg-cyan-400/10 border border-cyan-400/20 grid place-items-center text-cyan-300 shrink-0">
                  <b.Icon className="w-4 h-4" />
                </div>
                {b.t}
              </li>
            ))}
          </ul>

          <div className="mt-10 pt-8 border-t border-white/10 space-y-3 text-sm text-slate-400">
            <a href="mailto:intel@therevenuecycleiq.com" className="flex items-center gap-2.5 hover:text-white">
              <Mail className="w-4 h-4 text-cyan-300" /> intel@therevenuecycleiq.com
            </a>
            <a href="tel:+17089955104" className="flex items-center gap-2.5 hover:text-white">
              <Phone className="w-4 h-4 text-cyan-300" /> +1 (708) 995-5104
            </a>
            <div className="flex items-center gap-2.5">
              <MapPin className="w-4 h-4 text-cyan-300" /> Headquartered in the USA
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form data-testid="demo-form" onSubmit={onSubmit} className="rciq-glass-strong p-7 sm:p-9">
            {submitted ? (
              <div data-testid="demo-form-success" className="py-10 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-cyan-400/10 border border-cyan-400/20 grid place-items-center text-cyan-300">
                  <CheckCircle2 className="w-7 h-7" />
                </div>
                <h3 className="mt-5 font-display text-2xl font-semibold text-white">You&apos;re on the list.</h3>
                <p className="mt-2 text-slate-300 max-w-md mx-auto">
                  A revenue specialist will reach out within one business day.
                </p>
                <button
                  data-testid="demo-form-submit-another"
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-lg rciq-btn-ghost text-sm"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-slate-300 text-sm">Full name</Label>
                    <Input id="name" data-testid="form-input-name" placeholder="Jane Doe" value={form.name} onChange={(e) => update("name", e.target.value)} className="mt-1.5 h-11 bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500" required />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-slate-300 text-sm">Work email</Label>
                    <Input id="email" type="email" data-testid="form-input-email" placeholder="jane@bhprovider.com" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1.5 h-11 bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500" required />
                  </div>
                  <div>
                    <Label htmlFor="organization" className="text-slate-300 text-sm">Organization</Label>
                    <Input id="organization" data-testid="form-input-organization" placeholder="Acme Health Partners" value={form.organization} onChange={(e) => update("organization", e.target.value)} className="mt-1.5 h-11 bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500" required />
                  </div>
                  <div>
                    <Label htmlFor="role" className="text-slate-300 text-sm">Your role</Label>
                    <Input id="role" data-testid="form-input-role" placeholder="Director of Revenue Cycle" value={form.role} onChange={(e) => update("role", e.target.value)} className="mt-1.5 h-11 bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-slate-300 text-sm">Phone (optional)</Label>
                    <Input id="phone" data-testid="form-input-phone" placeholder="+1 (555) 123-4567" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-1.5 h-11 bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500" />
                  </div>
                  <div>
                    <Label htmlFor="org_size" className="text-slate-300 text-sm">Organization type</Label>
                    <Select value={form.organization_size} onValueChange={(v) => update("organization_size", v)}>
                      <SelectTrigger id="org_size" data-testid="form-input-size" className="mt-1.5 h-11 bg-white/[0.04] border-white/10 text-white">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Provider — single site">Provider — single site</SelectItem>
                        <SelectItem value="Provider — multi-site">Provider — multi-site</SelectItem>
                        <SelectItem value="Hospital / Health system">Hospital / Health system</SelectItem>
                        <SelectItem value="MSO">MSO</SelectItem>
                        <SelectItem value="Billing / RCM company">Billing / RCM company</SelectItem>
                        <SelectItem value="Consultant">Consultant</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-5">
                  <Label htmlFor="message" className="text-slate-300 text-sm">What would you like to solve?</Label>
                  <Textarea id="message" data-testid="form-input-message" rows={4} placeholder="e.g., We're seeing a spike in CO-197 authorization denials with UMR..." value={form.message} onChange={(e) => update("message", e.target.value)} className="mt-1.5 bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500" />
                </div>

                <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
                  <p className="text-[12px] text-slate-500">
                    By submitting, you agree to our terms and privacy policy. No PHI required.
                  </p>
                  <button
                    type="submit"
                    data-testid="form-submit-btn"
                    disabled={loading}
                    className="h-11 px-6 rounded-lg rciq-btn-primary text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
                  >
                    {loading ? "Submitting..." : (<>Request my demo <Send className="w-4 h-4" /></>)}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

/* ============================ FOOTER ========================= */
const Footer = ({ onBookDemo }) => (
  <footer data-testid="site-footer" className="relative bg-[#040712] border-t border-white/5">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="bg-white rounded-xl ring-1 ring-white/10 shadow-[0_8px_30px_-10px_rgba(34,211,238,0.35)] overflow-hidden inline-block p-2">
            <img src={LOGO_URL} alt="Revenue Cycle IQ — Insights that drive revenue" className="h-24 w-auto block" />
          </div>
          <p className="mt-5 text-sm leading-relaxed max-w-sm text-slate-400">
            AI denial intelligence for healthcare revenue cycle teams.
            Upload-based, PHI-free, EHR-free — and ready in minutes.
          </p>
          <a
            href="https://www.therevenuecycleiq.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-consulting-link"
            className="mt-4 inline-flex items-center gap-2 text-[12px] text-slate-400 hover:text-cyan-300 transition group"
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400/70 group-hover:bg-cyan-300" />
            Founded by Revenue Cycle IQ Consulting — 24+ years of hands-on RCM expertise
            <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">↗</span>
          </a>
          <div className="mt-5 flex items-center gap-3">
            <a href="mailto:intel@therevenuecycleiq.com" data-testid="social-email" aria-label="Email Revenue Cycle IQ" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-white/[0.04] hover:bg-cyan-400/10 text-slate-300 hover:text-cyan-300 border border-white/10 hover:border-cyan-400/30 transition text-sm">
              <Mail className="w-4 h-4" />
              <span>intel@therevenuecycleiq.com</span>
            </a>
          </div>
        </div>

        <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8 text-sm">
          <div>
            <div className="text-white font-semibold mb-4 font-display text-sm">Product</div>
            <ul className="space-y-2.5 text-slate-400">
              <li><a href="#benefits" className="hover:text-white">Platform</a></li>
              <li><a href="#dashboard" className="hover:text-white">Dashboard</a></li>
              <li><a href="#how-it-works" className="hover:text-white">How it works</a></li>
              <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
            </ul>
          </div>
          <div>
            <div className="text-white font-semibold mb-4 font-display text-sm">Company</div>
            <ul className="space-y-2.5 text-slate-400">
              <li><a href="https://www.therevenuecycleiq.com" target="_blank" rel="noopener noreferrer" className="hover:text-white">About</a></li>
              <li><a href="https://www.therevenuecycleiq.com/services" target="_blank" rel="noopener noreferrer" className="hover:text-white">Services</a></li>
              <li><button onClick={onBookDemo} data-testid="footer-contact-btn" className="hover:text-white">Contact</button></li>
            </ul>
          </div>
          <div>
            <div className="text-white font-semibold mb-4 font-display text-sm">Legal</div>
            <ul className="space-y-2.5 text-slate-400">
              <li><a data-testid="footer-privacy" href="https://www.therevenuecycleiq.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-white">Privacy Policy</a></li>
              <li><a data-testid="footer-terms" href="https://www.therevenuecycleiq.com/terms-of-service" target="_blank" rel="noopener noreferrer" className="hover:text-white">Terms of Service</a></li>
              <li><a data-testid="footer-disclaimer" href="https://www.therevenuecycleiq.com/disclaimer" target="_blank" rel="noopener noreferrer" className="hover:text-white">Disclaimer</a></li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-14 pt-8 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="text-xs text-slate-500">
          © {new Date().getFullYear()} Revenue Cycle IQ. All rights reserved.
        </div>
        <div className="text-xs text-slate-500 flex items-center gap-2 max-w-xl text-right">
          <ShieldCheck className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
          Revenue Cycle IQ is an upload-based denial analytics platform. It is not an EHR/PM integration platform, does not connect to live patient systems, and does not accept PHI.
        </div>
      </div>
    </div>
  </footer>
);

/* ============================ PAGE =========================== */
/* ====================== ATTESTATION MODAL ===================== */
const AGREEMENTS = [
  {
    id: "tos",
    title: "Terms of Service",
    href: "https://www.therevenuecycleiq.com/terms-of-service",
    summary: [
      "Revenue Cycle IQ provides operational denial analytics on a month-to-month subscription. You may cancel at any time — cancellation takes effect at the end of your current billing cycle.",
      "You agree to use the platform only for legitimate revenue cycle operations within your own organization. Sharing your account credentials with parties outside your organization is prohibited.",
      "The platform is provided 'as-is' as an operational intelligence tool. It is not claim adjudication software and does not replace payor-specific appeal review or financial accounting.",
    ],
  },
  {
    id: "privacy",
    title: "Privacy Policy",
    href: "https://www.therevenuecycleiq.com/privacy-policy",
    summary: [
      "We collect only the business-account information necessary to provide the service (name, email, organization, billing info). We do not sell your data.",
      "All processing happens on encrypted infrastructure with industry-standard security controls.",
      "You may request export or deletion of your business-account data at any time by emailing intel@therevenuecycleiq.com.",
    ],
  },
  {
    id: "no-phi",
    title: "No-PHI Data Policy",
    href: "https://www.therevenuecycleiq.com/disclaimer",
    summary: [
      "Revenue Cycle IQ is architected so PHI is never received, stored, or processed. Uploaded files are automatically screened — any file containing patient identifiers (names, MRNs, DOBs, SSNs, addresses, phone numbers) is rejected at the browser before transmission.",
      "Because no PHI ever reaches us, we are NOT a Business Associate under HIPAA. No BAA is required.",
      "You are responsible for ensuring exports from your billing system / clearinghouse are de-identified prior to upload. Our PHI screening is an additional safety check, not a substitute for your own data hygiene.",
    ],
  },
];

const AttestationModal = ({ plan, onClose }) => {
  const [agreed, setAgreed] = useState(false);
  const [expanded, setExpanded] = useState({});

  const planKey = plan?.key;
  useEffect(() => {
    if (planKey) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [planKey]);

  if (!plan) return null;

  const link = STRIPE_LINKS[plan.key]?.[plan.billing];
  const priceLabel = plan.billing === "annual"
    ? `$${plan.price.toLocaleString()}/mo · billed annually`
    : `$${plan.price.toLocaleString()}/mo`;

  const handleContinue = () => {
    if (!agreed || !link) return;
    try {
      localStorage.setItem(
        "rciq_attestation",
        JSON.stringify({
          plan: plan.key,
          name: plan.name,
          billing: plan.billing,
          price: plan.price,
          agreed_at: new Date().toISOString(),
          agreements: AGREEMENTS.map((a) => a.id),
        })
      );
    } catch {} // eslint-disable-line no-empty
    window.location.href = link;
  };

  const linkReady = link && !link.includes("REPLACE_");

  return (
    <div data-testid="attestation-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        data-testid="attestation-backdrop"
        onClick={onClose}
        className="absolute inset-0 bg-[#020611]/85 backdrop-blur-md"
      />
      {/* Panel */}
      <div className="relative w-full max-w-2xl max-h-[92vh] flex flex-col rounded-2xl border border-teal-400/30 bg-gradient-to-b from-[#0a1628] to-[#050913] shadow-[0_40px_120px_-20px_rgba(45,212,191,0.45),0_0_80px_-10px_rgba(34,211,238,0.25)] overflow-hidden">
        {/* Header */}
        <div className="relative px-6 sm:px-8 pt-7 pb-5 border-b border-white/8">
          <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-cyan-400 via-teal-400 to-cyan-400" />
          <button
            data-testid="attestation-close"
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 w-9 h-9 rounded-lg grid place-items-center text-slate-400 hover:text-white hover:bg-white/5 transition"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="text-[10px] tracking-[0.28em] uppercase text-teal-300 font-bold">Review &amp; Continue to Checkout</div>
          <h3 className="mt-2 font-display text-2xl sm:text-3xl font-semibold text-white tracking-tight">
            {plan.name} <span className="rciq-teal-text">— {priceLabel}</span>
          </h3>
          <p className="mt-2 text-sm text-slate-400 leading-relaxed">
            Month-to-month subscription. <span className="text-teal-300 font-semibold">Cancel anytime</span> — no long-term contracts. Please review and agree to the three policies below before continuing to secure checkout.
          </p>
          <div className="mt-3 inline-flex items-start gap-2 px-3 py-2 rounded-lg bg-teal-400/[0.08] border border-teal-400/25 text-[12.5px] text-teal-100 leading-relaxed">
            <Mail className="w-3.5 h-3.5 mt-0.5 text-teal-300 shrink-0" />
            <span>After payment, your dashboard login credentials will be emailed to you within <strong className="text-teal-200">4 business hours</strong>.</span>
          </div>
        </div>

        {/* Agreements scroll area */}
        <div className="px-6 sm:px-8 py-5 overflow-y-auto flex-1 space-y-3">
          {AGREEMENTS.map((a, idx) => {
            const isOpen = !!expanded[a.id];
            return (
              <div key={a.id} data-testid={`agreement-${a.id}`} className="rounded-xl border border-white/10 bg-white/[0.02]">
                <button
                  type="button"
                  onClick={() => setExpanded((p) => ({ ...p, [a.id]: !p[a.id] }))}
                  data-testid={`agreement-toggle-${a.id}`}
                  className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-7 h-7 rounded-lg grid place-items-center bg-teal-400/10 border border-teal-400/30 text-teal-300 font-bold text-xs shrink-0">
                      {idx + 1}
                    </div>
                    <div className="font-display font-semibold text-white text-[15px] truncate">{a.title}</div>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-4 sm:px-5 pb-4 -mt-1 space-y-2.5 text-[13px] leading-relaxed text-slate-300 border-t border-white/8 pt-3">
                    {a.summary.map((p, i) => <p key={i}>{p}</p>)}
                    <a
                      href={a.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-teal-300 hover:text-teal-200 text-[12.5px] font-medium pt-1"
                    >
                      View full {a.title}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 sm:px-8 py-5 border-t border-white/8 bg-[#040b18]">
          <label data-testid="attestation-checkbox-label" className="flex items-start gap-3 cursor-pointer select-none">
            <input
              data-testid="attestation-checkbox"
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-teal-400/50 bg-white/5 text-teal-400 focus:ring-teal-400 focus:ring-offset-0 accent-teal-400 cursor-pointer shrink-0"
            />
            <span className="text-[13.5px] text-slate-200 leading-relaxed">
              I have read and agree to the <strong className="text-white">Terms of Service</strong>, <strong className="text-white">Privacy Policy</strong>, and <strong className="text-white">No-PHI Data Policy</strong> above.
            </span>
          </label>

          {!linkReady && (
            <div className="mt-3 text-[12px] text-amber-300/90 bg-amber-400/[0.06] border border-amber-400/25 rounded-lg px-3 py-2">
              ⚠️ Stripe Payment Link for this plan is not configured yet. Once you create the Stripe products, paste the live links into <code className="font-mono text-amber-200">STRIPE_LINKS</code> at the top of <code className="font-mono text-amber-200">Landing.jsx</code>.
            </div>
          )}

          <div className="mt-4 flex flex-col-reverse sm:flex-row sm:items-center gap-2.5 sm:justify-end">
            <button
              data-testid="attestation-cancel"
              type="button"
              onClick={onClose}
              className="h-11 px-5 rounded-lg rciq-btn-ghost text-sm font-semibold"
            >
              Cancel
            </button>
            <button
              data-testid="attestation-continue"
              type="button"
              onClick={handleContinue}
              disabled={!agreed || !linkReady}
              className={`h-11 px-6 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2 transition ${
                agreed && linkReady
                  ? "rciq-btn-primary"
                  : "bg-white/5 text-slate-500 border border-white/10 cursor-not-allowed"
              }`}
            >
              Continue to Secure Checkout
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ============================== LANDING ====================== */
const Landing = () => {
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollToPreview = () => {
    const el = document.getElementById("ai-preview");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="landing-page" className="min-h-screen bg-[var(--rciq-bg)] text-white">
      <Toaster theme="dark" position="top-right" richColors closeButton />
      <Nav onUpload={scrollToPreview} onBookDemo={scrollToContact} />
      <main>
        <Hero onUpload={scrollToPreview} onBookDemo={scrollToContact} />
        <IndustryAnchor onUpload={scrollToPreview} />
        <TrustValue />
        <Benefits />
        <AIPreview onBookDemo={scrollToContact} />
        <DashboardPreview />
        <HowItWorks onUpload={scrollToPreview} />
        <AIInsights />
        <Security />
        <ARTrends onBookDemo={scrollToContact} />
        <Pricing onBookDemo={scrollToContact} onUpload={scrollToPreview} onCheckout={setCheckoutPlan} />
        <FAQ />
        <FinalCTA onUpload={scrollToPreview} onBookDemo={scrollToContact} />
        <Contact />
      </main>
      <Footer onBookDemo={scrollToContact} />
      <AttestationModal key={checkoutPlan?.key || "none"} plan={checkoutPlan} onClose={() => setCheckoutPlan(null)} />
    </div>
  );
};

export default Landing;
